import React, { useContext, memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { Grid, FormControl } from '@mui/material';
import { Autocomplete, TextField, Button, Typography } from '@totalsoft/rocket-ui';
import DetailedTextField from 'components/common/inputs/DetailedTextField';
import DetailedSlider from 'components/common/inputs/DetailedSlider';
import { RightsContext } from 'providers/RightsContext';
import { ParamsContext } from 'providers/ParamsContext';
import { interestType } from 'features/offer/constants';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';

const FacilityOfferFinancingStructureComponent = ({
  document,
  isDirty,
  validationInfo,
  t,
  maxResidualValuePercentLoading,
  saving,
  onSave,
  onPropertyChange,
  offerFinancialProductRuleReadOnlyFields,
  interestTypes,
  contractTypes,
  referenceInterests
}) => {
  const fixedInterestId = interestTypes.find(int => int.code === interestType.FIX)?.id;
  const variableInterestId = interestTypes.find(int => int.code === interestType.VAR)?.id;
  const [localReferenceInterests, setLocalReferenceInterests] = useState(referenceInterests);
  useEffect(() => {
    if (referenceInterests.length > 0) {
      var filteredOfferReferenceInterests = [];
      filteredOfferReferenceInterests = referenceInterests.filter(
        m => m.isFixed === document.isFixed && m.currencyCode === document.currencyCode
      );
      setLocalReferenceInterests(filteredOfferReferenceInterests);
    }
  }, [referenceInterests, document.isFixed, document.currencyCode]);

  const { financingStructureWrite, readOnlyFull, isImage } = useContext(RightsContext);
  const { cvsChPercent, cvsChAmount } = useContext(ParamsContext);

  const contractType = useMemo(
    () => contractTypes?.find(type => type.contractTypeId === document.contractTypeId),
    [contractTypes, document]
  );

  return (
    <form noValidate>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={4} px={1}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id={'offerFinancingStructureCard_contractTypeName'}
            fullWidth
            name='channelType'
            value={contractType || ''}
            valueKey='contractTypeId'
            labelKey='contractTypeName'
            label={t('OfferEdit.FinancingStructure.ContractTypeName')}
            options={contractTypes}
            onChange={onPropertyChange('contractTypeId')}
            disabled={readOnlyFull || !financingStructureWrite}
            isClearable={false}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            id={'offerFinancingStructureCard_currency'}
            disabled={true}
            fullWidth
            value={document.currencyCode || ''}
            label={t('OfferEdit.FinancingStructure.Currency')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            id={'offerFinancingStructureCard_totalAcquisitionPrice'}
            disabled
            fullWidth
            isNumeric
            value={document.totalAcquisitionPrice || ''}
            inputProps={{ decimalScale: cvsChAmount }}
            label={t('OfferEdit.FinancingStructure.TotalAcquisitionPrice')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            id={'offerFinancingStructureCard_interestType'}
            fullWidth
            value={document.isFixed ? fixedInterestId ?? ' ' : variableInterestId ?? ' '}
            valueKey='id'
            labelKey='name'
            label={t('OfferEdit.AdditionalData.InterestType')}
            options={interestTypes}
            onChange={onPropertyChange('isFixed')}
            isClearable={false}
            simpleValue={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            id={'offerFinancingStructureCard_referenceInterestType'}
            fullWidth
            value={document.interestTypeId || ''}
            valueKey='interestTypeId'
            labelKey='interestTypeName'
            label={t('OfferEdit.AdditionalData.ReferenceInterestType')}
            options={localReferenceInterests}
            onChange={onPropertyChange('interestTypeId')}
            isClearable={false}
            simpleValue={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedTextField
            id={'offerFinancingStructureCard_interestPercent'}
            value={document.interestPercent || ''}
            defaultValue={document.defaultInterestPercent}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultInterestPercent
            })}
            fullWidth
            helperText={validationInfo.interestPercent}
            error={Boolean(validationInfo.interestPercent)}
            onChange={onPropertyChange('interestPercent')}
            decimalScale={cvsChPercent}
            label={t('OfferEdit.FinancingStructure.InterestPercent')}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.interestPercentReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedSlider
            id={'offerFinancingStructureCard_advancePercent'}
            value={document.advancePercent || 0}
            defaultValue={document.defaultAdvancePercent}
            min={document.minAdvancePercent}
            max={document.maxAdvancePercent}
            step={1}
            decimalScale={cvsChPercent}
            label={t('OfferEdit.FinancingStructure.AdvancePercent')}
            onChange={onPropertyChange('advancePercent')}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultAdvancePercent
            })}
            fullWidth
            error={Boolean(validationInfo.advancePercent)}
            helperText={validationInfo.advancePercent}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.advancePercentReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedTextField
            id={'offerFinancingStructureCard_advance'}
            value={document.advance == null ? '' : document.advance}
            decimalScale={cvsChAmount}
            defaultValue={document.defaultAdvance}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultAdvance
            })}
            fullWidth
            error={Boolean(validationInfo.advance)}
            helperText={validationInfo.advance}
            onChange={onPropertyChange('advance')}
            label={t('OfferEdit.FinancingStructure.Advance')}
            disabled={readOnlyFull || !financingStructureWrite}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedSlider
            fullWidth
            id={'offerFinancingStructureCard_financingValuePercent'}
            value={document.financingValuePercent || 0}
            min={document.minAdvancePercent}
            max={document.maxAdvancePercent}
            step={1}
            label={t('OfferEdit.FinancingStructure.FinancingValuePercent')}
            onChange={onPropertyChange('financingValuePercent')}
            decimalScale={cvsChPercent}
            error={Boolean(validationInfo.financingValuePercent)}
            helperText={validationInfo.financingValuePercent}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.financedPercentReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            id={'offerFinancingStructureCard_financingValue'}
            disabled
            inputProps={{ decimalScale: cvsChAmount }}
            fullWidth
            isNumeric
            value={document.financingValue || 0}
            label={t('OfferEdit.FinancingStructure.FinancingValue')}
          />
        </Grid>
        {!document.hideInfoForDealersByContractTypes && (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              {maxResidualValuePercentLoading ? (
                <FormControl fullWidth>
                  <Typography sx={{ mb: 2 }}>{t('OfferEdit.FinancingStructure.ResidualValuePercent')}</Typography>
                  <WrappedLinearProgress />
                </FormControl>
              ) : (
                <DetailedSlider
                  id={'offerFinancingStructureCard_residualValuePercent'}
                  value={document.residualValuePercent || 0}
                  defaultValue={document.defaultResidualValuePercent}
                  min={document.minResidualValuePercent}
                  max={document.maxResidualValuePercent}
                  step={1}
                  decimalScale={cvsChPercent}
                  label={t('OfferEdit.FinancingStructure.ResidualValuePercent')}
                  onChange={onPropertyChange('residualValuePercent')}
                  auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                    data: document.defaultResidualValuePercent
                  })}
                  fullWidth
                  error={Boolean(validationInfo.residualValuePercent)}
                  helperText={validationInfo.residualValuePercent}
                  disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.rezidualPercentReadOnly}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              {maxResidualValuePercentLoading ? (
                <FormControl fullWidth>
                  <Typography sx={{ mb: 2 }}>{t('OfferEdit.FinancingStructure.ResidualValue')}</Typography>
                  <WrappedLinearProgress />
                </FormControl>
              ) : (
                <DetailedTextField
                  id={'offerFinancingStructureCard_residualValue'}
                  value={document.residualValue == null ? '' : document.residualValue}
                  decimalScale={cvsChAmount}
                  defaultValue={document.defaultResidualValue}
                  auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
                    data: document.defaultResidualValue
                  })}
                  fullWidth
                  error={Boolean(validationInfo.residualValue)}
                  helperText={validationInfo.residualValue}
                  onChange={onPropertyChange('residualValue')}
                  label={t('OfferEdit.FinancingStructure.ResidualValue')}
                  disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.rezidualAmountReadOnly}
                />
              )}
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedSlider
            id={'offerFinancingStructureCard_managementFeePercent'}
            value={document.managementFeePercent || 0}
            defaultValue={document.defaultManagementFeePercent}
            min={document.minManagementFeePercent}
            max={document.maxManagementFeePercent}
            step={1}
            decimalScale={cvsChPercent}
            label={t('OfferEdit.FinancingStructure.ManagementFeePercent')}
            onChange={onPropertyChange('managementFeePercent')}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultManagementFeePercent
            })}
            fullWidth
            error={Boolean(validationInfo.managementFeePercent)}
            helperText={validationInfo.managementFeePercent}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.managementFeePercentReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <TextField
            fullWidth
            isNumeric
            id={'offerFinancingStructureCard_managementFee'}
            value={document.managementFee == null ? '' : document.managementFee}
            inputProps={{ decimalScale: cvsChAmount }}
            error={validationInfo && validationInfo.managementFee ? true : false}
            helperText={validationInfo?.managementFee}
            onChange={onPropertyChange('managementFee')}
            label={t('OfferEdit.FinancingStructure.ManagementFee')}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.managementFeeReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DetailedSlider
            id={'offerFinancingStructureCard_durationMonths'}
            value={document.durationMonths || 0}
            defaultValue={document.defaultDuration}
            min={document.minDurationMonths}
            max={document.maxDurationMonths}
            step={1}
            label={t('OfferEdit.FinancingStructure.DurationMonths')}
            onChange={onPropertyChange('durationMonths')}
            auxiliaryInfo={t('OfferEdit.FinancingStructure.InitialValue', {
              data: document.defaultDuration
            })}
            fullWidth
            error={Boolean(validationInfo.durationMonths)}
            helperText={validationInfo.durationMonths}
            decimalScale={0}
            disabled={readOnlyFull || !financingStructureWrite || offerFinancialProductRuleReadOnlyFields.durationReadOnly}
          />
        </Grid>
        {document.logisticTaxExists && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'offerFinancingStructureCard_logisticTax'}
              disabled={readOnlyFull || document.logisticTax.isReadOnly}
              fullWidth
              isNumeric
              inputProps={{ decimalScale: cvsChAmount }}
              value={document.logisticTax.value || ''}
              onChange={onPropertyChange('logisticTax.value')}
              label={t('OfferEdit.FinancingStructure.LogisticTax')}
              helperText={validationInfo.logisticTax && validationInfo.logisticTax.value}
              error={validationInfo.logisticTax && !validationInfo.logisticTax.isValid}
            />
          </Grid>
        )}
        {!isImage && (
          <GridActionsWrapper item xs={12}>
            <Button
              id={'offerFinancingStructureCard_saveBtn'}
              color='primary'
              variant='contained'
              onClick={onSave}
              disabled={readOnlyFull || saving || !validationInfo.isValid || !financingStructureWrite || !isDirty}
            >
              {saving ? t('Saving') + '...' : t('Save')}
            </Button>
          </GridActionsWrapper>
        )}
      </Grid>
    </form>
  );
};

FacilityOfferFinancingStructureComponent.propTypes = {
  document: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  maxResidualValuePercentLoading: PropTypes.bool.isRequired,
  offerFinancialProductRuleReadOnlyFields: PropTypes.object.isRequired,
  interestTypes: PropTypes.array,
  contractTypes: PropTypes.array,
  referenceInterests: PropTypes.array.isRequired
};

export default withTranslation()(memo(FacilityOfferFinancingStructureComponent));
